import { useEffect, useState, useMemo } from 'react'
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useTheme } from '@/contexts/ThemeContext'

interface GuestSpot {
  city: string
  country: string
  start_date: string | null
  end_date: string | null
  lat?: number
  lon?: number
}

interface EnrichedArtist {
  username: string
  name: string
  followers: number
  bio: string
  profile_image_url: string
  home_city: string
  home_country: string | null
  home_lat?: number
  home_lon?: number
  guestSpots: GuestSpot[]
  network_score: number
}

type ViewMode = 'artists' | 'guests' | 'both'

interface GlobalGuestArtistsMapProps {
  artists: EnrichedArtist[]
  viewMode: ViewMode
}

// Create custom home city icon with dynamic sizing based on artist count
const createHomeCityIcon = (count: number) => {
  // Logarithmic scaling: size = baseSize + log2(count) * scaleMultiplier
  // For count=1: 24px (no padding), for count=100: ~51px (roughly double)
  const baseSize = 24
  const scaleMultiplier = 4
  const size = count === 1 ? baseSize : baseSize + Math.log2(count) * scaleMultiplier
  
  // Scale border width proportionally (minimum 2px, maximum 4px)
  const borderWidth = count === 1 ? 2 : Math.min(4, 2 + size / 20)
  
  // Scale font size proportionally (minimum 11px, maximum 16px)
  const fontSize = count === 1 ? 11 : Math.min(16, 11 + size / 10)
  
  return L.divIcon({
    html: `
      <div class="home-city-marker">
        <div class="home-city-dot" style="width: ${size}px; height: ${size}px; border-width: ${borderWidth}px;">
          <span class="home-city-count" style="font-size: ${fontSize}px;">${count}</span>
        </div>
      </div>
    `,
    className: 'home-city-marker-wrapper',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

// Create custom guest spot icon
const createGuestSpotIcon = () => {
  return L.divIcon({
    html: `
      <div class="guest-spot-marker">
        <div class="guest-spot-dot"></div>
      </div>
    `,
    className: 'guest-spot-marker-wrapper',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  })
}

// Calculate distance between two points using Haversine formula (returns km)
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const earthRadiusKm = 6371
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return earthRadiusKm * c
}

// Component to auto-fit bounds
function MapBoundsHandler({ artists, viewMode }: { artists: EnrichedArtist[], viewMode: ViewMode }) {
  const map = useMap()

  useEffect(() => {
    if (artists.length === 0) return

    const bounds: [number, number][] = []
    
    // Add all home cities (for 'artists' and 'both' modes)
    if (viewMode === 'artists' || viewMode === 'both') {
      artists.forEach(artist => {
        if (artist.home_lat && artist.home_lon) {
          bounds.push([artist.home_lat, artist.home_lon])
        }
      })
    }
    
    // Add all guest spots (for 'guests' and 'both' modes)
    if (viewMode === 'guests' || viewMode === 'both') {
      artists.forEach(artist => {
        if (artist.guestSpots && artist.guestSpots.length > 0) {
          artist.guestSpots.forEach(spot => {
            if (spot.lat && spot.lon) {
              bounds.push([spot.lat, spot.lon])
            }
          })
        }
      })
    }

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] })
    }
  }, [artists, map, viewMode])

  return null
}

export function GlobalGuestArtistsMap({ artists, viewMode }: GlobalGuestArtistsMapProps) {
  const [mapReady, setMapReady] = useState(false)
  const { currentThemeMode } = useTheme()

  console.log('[GlobalGuestArtistsMap] render — artists:', artists.length, '| viewMode:', viewMode, '| mapReady:', mapReady)

  // Group artists by geographic proximity (20km radius clustering)
  const homeCityGroups = useMemo(() => {
    const CLUSTER_RADIUS_KM = 20
    const clusters: Array<{
      lat: number
      lon: number
      artists: EnrichedArtist[]
    }> = []

    const artistsToCluster = artists.filter(artist => artist.home_lat && artist.home_lon)

    artistsToCluster.forEach(artist => {
      if (!artist.home_lat || !artist.home_lon) return
      
      // Find existing cluster within radius
      let foundCluster = false
      for (const cluster of clusters) {
        const distance = calculateDistance(
          artist.home_lat, 
          artist.home_lon, 
          cluster.lat, 
          cluster.lon
        )
        
        if (distance <= CLUSTER_RADIUS_KM) {
          cluster.artists.push(artist)
          // Recalculate centroid
          const totalLat = cluster.artists.reduce((sum, a) => sum + a.home_lat!, 0)
          const totalLon = cluster.artists.reduce((sum, a) => sum + a.home_lon!, 0)
          cluster.lat = totalLat / cluster.artists.length
          cluster.lon = totalLon / cluster.artists.length
          foundCluster = true
          break
        }
      }
      
      // If no cluster found within radius, create new cluster
      if (!foundCluster) {
        clusters.push({
          lat: artist.home_lat,
          lon: artist.home_lon,
          artists: [artist]
        })
      }
    })

    return clusters
  }, [artists])

  // Create lines data (home to guest spot with offset)
  const travelLines = useMemo(() => {
    const lines: Array<{
      id: string
      artist: EnrichedArtist
      spot: GuestSpot
      coordinates: [number, number][]
    }> = []

    artists.forEach(artist => {
      if (!artist.home_lat || !artist.home_lon || !artist.guestSpots) return
      
      artist.guestSpots.forEach((spot, index) => {
        if (!spot.lat || !spot.lon) return
        
        // For 'guests' mode only, use original coordinates (no offset)
        // For 'both' mode, use offset coordinates to avoid overlap
        const endLat = viewMode === 'guests' ? spot.lat : spot.lat - 0.15
        const endLon = viewMode === 'guests' ? spot.lon : spot.lon + 0.05
        
        lines.push({
          id: `${artist.username}-${index}`,
          artist,
          spot,
          coordinates: [
            [artist.home_lat!, artist.home_lon!],
            [endLat, endLon]
          ]
        })
      })
    })

    return lines
  }, [artists, viewMode])

  // Get all unique guest spot locations with offset to avoid overlap
  const guestSpotLocations = useMemo(() => {
    const spots = new Map<string, {
      lat: number
      lon: number
      city: string
      country: string
      artists: Array<{ artist: EnrichedArtist; spot: GuestSpot }>
    }>()

    artists.forEach(artist => {
      if (!artist.guestSpots) return
      
      artist.guestSpots.forEach(spot => {
        if (!spot.lat || !spot.lon) return
        
        const key = `${spot.lat},${spot.lon}`
        
        if (!spots.has(key)) {
          spots.set(key, {
            lat: spot.lat,
            lon: spot.lon,
            city: spot.city,
            country: spot.country,
            artists: []
          })
        }
        
        spots.get(key)!.artists.push({ artist, spot })
      })
    })

    // Apply offset to guest spot markers to avoid overlap with home cities (only for 'both' mode)
    // For 'guests' mode, keep original coordinates
    return Array.from(spots.values()).map(spot => ({
      ...spot,
      lat: viewMode === 'guests' ? spot.lat : spot.lat - 0.15,
      lon: viewMode === 'guests' ? spot.lon : spot.lon + 0.05
    }))
  }, [artists, viewMode])

  const centerCoordinates: [number, number] = [20, 0] // World center

  return (
    <>
      <style>{`
        /* Home city marker styles */
        .home-city-marker-wrapper {
          background: none !important;
          border: none !important;
        }
        
        .home-city-marker {
          position: relative;
          cursor: pointer;
        }
        
        .home-city-dot {
          background: #3b82f6;
          border: solid ${currentThemeMode === 'dark' ? '#2a2a2a' : 'white'};
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px ${currentThemeMode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.2)'};
          transition: transform 0.2s;
        }
        
        .home-city-dot:hover {
          transform: scale(1.1);
        }
        
        .home-city-count {
          color: white;
          font-weight: 700;
        }
        
        /* Guest spot marker styles */
        .guest-spot-marker-wrapper {
          background: none !important;
          border: none !important;
        }
        
        .guest-spot-marker {
          position: relative;
          cursor: pointer;
        }
        
        .guest-spot-dot {
          width: 20px;
          height: 20px;
          background: #ef4444;
          border: 2px solid ${currentThemeMode === 'dark' ? '#2a2a2a' : 'white'};
          border-radius: 50%;
          box-shadow: 0 2px 4px ${currentThemeMode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.2)'};
          transition: transform 0.2s;
        }
        
        .guest-spot-dot:hover {
          transform: scale(1.2);
        }
        
        /* Custom popup styles */
        .leaflet-popup-content-wrapper {
          border-radius: 8px;
          padding: 0;
          background-color: ${currentThemeMode === 'dark' ? 'rgba(42, 42, 42, 0.95)' : 'white'} !important;
        }
        
        .leaflet-popup-content {
          margin: 12px;
          min-width: 200px;
          color: ${currentThemeMode === 'dark' ? '#ffffff' : '#111827'} !important;
        }
        
        .popup-artist-name {
          font-weight: 600;
          font-size: 14px;
          margin-bottom: 4px;
          color: ${currentThemeMode === 'dark' ? '#ffffff' : '#111827'};
        }
        
        .popup-location {
          font-size: 12px;
          color: ${currentThemeMode === 'dark' ? '#a0a0a0' : '#6b7280'};
          margin-bottom: 2px;
        }
        
        .popup-dates {
          font-size: 11px;
          color: ${currentThemeMode === 'dark' ? '#a0a0a0' : '#9ca3af'};
          font-style: italic;
        }
        
        .popup-followers {
          font-size: 11px;
          color: ${currentThemeMode === 'dark' ? '#a0a0a0' : '#9ca3af'};
          margin-top: 4px;
        }

        .popup-city-header {
          font-weight: 700;
          font-size: 16px;
          margin-bottom: 8px;
          border-bottom: 2px solid ${currentThemeMode === 'dark' ? '#3a3a3a' : '#e5e7eb'};
          padding-bottom: 4px;
          color: ${currentThemeMode === 'dark' ? '#ffffff' : '#111827'};
        }

        .popup-artist-list {
          max-height: 200px;
          overflow-y: auto;
        }

        .popup-artist-item {
          padding: 6px 0;
          border-bottom: 1px solid ${currentThemeMode === 'dark' ? '#3a3a3a' : '#f3f4f6'};
        }

        .popup-artist-item:last-child {
          border-bottom: none;
        }

        .global-guest-artists-map .leaflet-container {
          background: hsl(var(--background));
        }

        /* Apply grayscale to map tiles */
        .global-guest-artists-map .leaflet-tile {
          filter: ${currentThemeMode === 'dark' ? 'grayscale(100%) contrast(1.1) brightness(0.65)' : 'grayscale(100%) contrast(1.1)'};
        }

        ${currentThemeMode === 'dark' ? `
          .global-guest-artists-map .leaflet-control-attribution,
          .global-guest-artists-map .leaflet-tooltip {
            background-color: rgba(42, 42, 42, 0.95) !important;
            color: #ffffff !important;
          }

          .global-guest-artists-map .leaflet-popup-tip {
            background-color: rgba(42, 42, 42, 0.95) !important;
          }

          .global-guest-artists-map .leaflet-control-zoom a {
            background-color: #2a2a2a !important;
            color: #ffffff !important;
            border-color: #3a3a3a !important;
          }

          .global-guest-artists-map .leaflet-control-zoom a:hover {
            background-color: #333333 !important;
          }
        ` : ''}
      `}</style>
      
      <div
        className="w-full h-full rounded-lg overflow-hidden border border-border global-guest-artists-map"
        ref={el => {
          if (el) console.log('[GlobalGuestArtistsMap] wrapper size:', el.offsetWidth, 'x', el.offsetHeight)
        }}
      >
        <MapContainer
          center={centerCoordinates}
          zoom={2}
          style={{ height: '100%', width: '100%' }}
          className="z-10"
          zoomControl={window.innerWidth >= 768}
          scrollWheelZoom={false}
          whenReady={() => {
            console.log('[GlobalGuestArtistsMap] map ready')
            setMapReady(true)
          }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          
          {/* Auto-adjust bounds based on artists */}
          {mapReady && <MapBoundsHandler artists={artists} viewMode={viewMode} />}
          
          {/* Draw lines from home to guest spots (only for 'guests' and 'both' modes) */}
          {(viewMode === 'guests' || viewMode === 'both') && travelLines.map(line => (
            <Polyline
              key={line.id}
              positions={line.coordinates}
              color="#ef4444"
              weight={2}
              opacity={0.6}
            />
          ))}
          
          {/* Home city markers (only for 'artists' and 'both' modes) */}
          {(viewMode === 'artists' || viewMode === 'both') && homeCityGroups.map((group, index) => (
            <Marker
              key={`home-${index}`}
              position={[group.lat, group.lon]}
              icon={createHomeCityIcon(group.artists.length)}
            >
              <Popup maxWidth={300}>
                <div className="popup-city-header">
                  {group.artists[0].home_city}, {group.artists[0].home_country || 'Unknown'}
                </div>
                <div className="popup-artist-list">
                  {group.artists.map((artist, idx) => (
                    <div key={idx} className="popup-artist-item">
                      <div className="popup-artist-name">@{artist.username}</div>
                      <div className="popup-followers">
                        {artist.followers.toLocaleString()} followers
                      </div>
                      {artist.guestSpots && artist.guestSpots.length > 0 && (
                        <div className="popup-dates">
                          {artist.guestSpots.length} guest {artist.guestSpots.length === 1 ? 'spot' : 'spots'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Popup>
            </Marker>
          ))}
          
          {/* Guest spot markers (only for 'guests' and 'both' modes) */}
          {(viewMode === 'guests' || viewMode === 'both') && guestSpotLocations.map((location, index) => (
            <Marker
              key={`guest-${index}`}
              position={[location.lat, location.lon]}
              icon={createGuestSpotIcon()}
            >
              <Popup maxWidth={300}>
                <div className="popup-city-header">
                  {location.city}, {location.country}
                </div>
                <div className="popup-artist-list">
                  {location.artists.map(({ artist, spot }, idx) => (
                    <div key={idx} className="popup-artist-item">
                      <div className="popup-artist-name">@{artist.username}</div>
                      <div className="popup-location">
                        from {artist.home_city}
                      </div>
                      {(spot.start_date || spot.end_date) && (
                        <div className="popup-dates">
                          {spot.start_date && spot.end_date
                            ? `${new Date(spot.start_date).toLocaleDateString()} - ${new Date(spot.end_date).toLocaleDateString()}`
                            : spot.start_date
                            ? `From ${new Date(spot.start_date).toLocaleDateString()}`
                            : spot.end_date
                            ? `Until ${new Date(spot.end_date).toLocaleDateString()}`
                            : 'Dates TBA'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
        
        {artists.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-20">
            <div className="text-center">
              <p className="text-muted-foreground">
                No guest artist data available
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}



